import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  linkMembershipFail,
  unlinkMembershipFail,
} from '@features/membership/store/actions/membership.actions';
import { LoadPointsSummary } from '@features/points-summary/store/actions/points-summary.actions';
import { PollOrder } from '@features/polling/store/polling.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { SentryService } from '@rd-lib/sentry/sentry.service';
import { PointsTransferApiService } from '@rd/core/services/points-transfer-api/points-transfer-api.service';
import { DialogService } from '@rd/popup/services/dialog/dialog.service';
import { CheckoutErrorDialogComponent } from '@rd/shared-legacy/components/checkout-error-dialog/checkout-error-dialog.component';
import {
  CheckoutErrorDialogConfig,
  HttpError,
  PointsTransferMetadata,
} from '@rd/types';
import { catchError, concatMap, map, of, switchMap, tap } from 'rxjs';
import { EverydaySpendActions } from '../actions/everyday-spend.actions';

@Injectable()
export class EverydaySpendEffects {
  private router = inject(Router);
  private actions$ = inject(Actions);
  private dialogService = inject(DialogService);
  private sentryService = inject(SentryService);
  private pointsTransferApiService = inject(PointsTransferApiService);

  linkMembershipFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(linkMembershipFail),
        tap(({ error }) => {
          this.openCheckoutErrorDialog('link', error);
        }),
      ),
    {
      dispatch: false,
    },
  );

  unlinkMembershipFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(unlinkMembershipFail),
        tap(({ error }) => {
          this.openCheckoutErrorDialog('unlink', error);
        }),
      ),
    {
      dispatch: false,
    },
  );

  submitPointsTransfer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EverydaySpendActions.submitPointsTransfer),
      switchMap(({ loyaltyProgramId, formData }) => {
        const { sourceAmount: transferAmount, membershipId } = formData;

        return this.pointsTransferApiService
          .postPointsTransfer({
            pointsTransfer: {
              loyaltyProgramId,
              membershipId,
              transferAmount,
            },
          })
          .pipe(
            map((pointsTransaction) =>
              EverydaySpendActions.submitPointsTransferSuccess({
                pointsTransaction,
              }),
            ),
            catchError((error: HttpError) =>
              of(
                EverydaySpendActions.submitPointsTransferFailure({
                  error,
                }),
              ),
            ),
          );
      }),
    ),
  );

  submitPointsTransferSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EverydaySpendActions.submitPointsTransferSuccess),
      concatMap(({ pointsTransaction }) => {
        const { loyaltyProgramId } =
          pointsTransaction.metadata as PointsTransferMetadata;

        const orderId = pointsTransaction.orderId;

        const finalActions = [
          EverydaySpendActions.clearCheckoutFormData({ loyaltyProgramId }),
          new LoadPointsSummary(),
        ];

        if (!orderId) {
          this.sentryService.manualError(
            'No orderId in PointsTransfer response',
            {
              pointsTransactionId: pointsTransaction.id,
            },
          );

          return finalActions;
        }

        this.router.navigate(['everyday_rewards/success', orderId]);

        return [PollOrder({ orderId }), ...finalActions];
      }),
    ),
  );

  submitPointsTransferFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EverydaySpendActions.submitPointsTransferFailure),
      switchMap(
        ({ error }) =>
          this.dialogService.open(CheckoutErrorDialogComponent, {
            data: { error, iconName: 'unexpected-error' },
            id: 'everyday-spend-review-error-message',
            screenMode: 'center-md',
          }).afterClosed,
      ),
      switchMap(() => of(EverydaySpendActions.clearPointsTransferError())),
    ),
  );

  private openCheckoutErrorDialog(
    type: 'link' | 'unlink',
    error: HttpError,
  ): void {
    const errorData = this.getErrorDialogData(type, error);

    this.dialogService
      .open(CheckoutErrorDialogComponent, {
        data: errorData,
        id: `rd-${type}-membership-checkout-error-dialog`,
        screenMode: 'center-md',
        ariaLabel: `Failed to ${type} membership`,
      })
      .afterClosed.subscribe(() => {
        // [Hau] Clear the query params to make sure when user
        // relink the card again, these query params are not passed as redirectUrl
        // causing the error to pop up again even though link is successful
        this.router.navigate([], {
          queryParams: {
            type: null,
            errorCode: null,
            errorMessage: null,
          },
          queryParamsHandling: 'merge',
        });
      });
  }

  private getErrorDialogData(
    type: 'link' | 'unlink',
    error: HttpError,
  ): CheckoutErrorDialogConfig {
    let errorData = {
      ctaButtonText: 'Try again',
      errorMessageDescription:
        'An unexpected error has occurred and we’re unable to process your request. We’re working on it – please try again later.',
      errorMessageTitle: `Failed to ${type} membership`,
    };

    switch (type) {
      case 'link': {
        if (error?.errors?.[0]?.code === 'GH0004') {
          // [Hau] Special case for GH0004 error code
          // Ref: https://kaligo.slack.com/archives/C093TNN7SUF/p1753760961319059
          errorData = {
            ctaButtonText: 'OK',
            errorMessageTitle: `Card already linked`,
            errorMessageDescription: `
                This Everyday Rewards card is currently linked to another ANZ Rewards account. 
                <p></p>
                To continue, please either use a different Everyday Rewards card or unlink this card from the other ANZ Rewards account before trying again.
              `,
          };
        }

        break;
      }

      case 'unlink': {
        if (error.status === 409) {
          // Special case for 409 error code
          // Ref: https://kaligo.slack.com/archives/C093TNN7SUF/p1754985882524559?thread_ts=1754984390.463749&cid=C093TNN7SUF
          errorData = {
            ctaButtonText: 'OK',
            errorMessageTitle: `Failed to unlink membership`,
            errorMessageDescription:
              'We are unable to unlink your card because there is an active auto-redemption set for it. Please cancel the auto-redemption under My Redemptions before trying to unlink the card again.',
          };
        }

        break;
      }
    }

    return errorData;
  }
}
